import { Injectable, OnDestroy } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  Subject,
  Subscription,
  forkJoin,
  from,
  of,
  timer,
} from 'rxjs';
import { catchError, finalize, map, mergeMap, switchMap, takeUntil, tap } from 'rxjs/operators';
import {
  ConciliadoraRegistro,
  ConciliadoraTipoConfig,
  ExecutarOpts,
  FlowContextoAcao,
  FlowExecutionStep,
  FlowExecLogFinishRequest,
  FlowExecLogStartRequest,
  FlowJobStatus,
  FlowJobStatusType,
  FlowMonitorState,
} from '../models/conciliadora.models';
import {
  FlowExecutorJob,
  FlowExecutorJobResult,
  FlowExecutorOptions,
  FlowExecutorStrategy,
  FlowExecutorSummary,
  MultiJobMonitorState,
  PersistedActiveJob,
} from '../models/flow-executor.models';
import { ConciliadoraService } from './conciliadora.service';

/**
 * FlowExecutorService - orquestrador unificado de execucao de fluxos.
 *
 * Substitui (gradualmente) os caminhos espalhados em conciliadora-main:
 *  - `executeProcessamento` (loop sequencial)
 *  - `dispararFluxoNewModeBatch` (so caso "1 IDCONF NEWMODE")
 *  - `executeLocalizarNota` (NEWMODE async direto)
 *  - chamada direta a `executarConfiguracao` por linha
 *
 * Origem: rules_conciliadora/executor_unificado_fluxo.plan.md (Fase 2).
 *
 * Decisoes ja absorvidas:
 *  - Q1: concurrency default = 4
 *  - Q2: estrategia LEGACY_ASYNC via runActionAsync (com fallback LEGACY_SYNC)
 *  - Q3: persistencia em MKTPFLOWEXECLOG (best-effort, nao bloqueia)
 *  - Q4: estrategia CLIENT_WORKER (implementacao real na Fase 7)
 *
 * IMPORTANTE: este servico mantem seu proprio estado multi-job (Map). O
 * `FlowMonitorService` atual continua existindo para o caminho legado "Localizar
 * Nota" durante a migracao. Na Fase 3 ele sera refatorado para ser
 * complementar a este executor.
 */
@Injectable({ providedIn: 'root' })
export class FlowExecutorService implements OnDestroy {
  private static readonly POLL_INTERVAL_MS = 1500;
  private static readonly DEFAULT_CONCURRENCY = 4;
  private static readonly DEFAULT_CHUNK_SIZE_NEWMODE = 200;
  private static readonly DEFAULT_CHUNK_SIZE_LEGACY = 50;
  private static readonly STORAGE_KEY_GROUPS = 'mktp.conciliadora.flowExecutor.activeGroups';

  /** Estado por job (jobId local). */
  private readonly jobStates = new Map<string, BehaviorSubject<FlowMonitorState>>();

  /** Estado agregado do agrupamento atual (1 rodada de execute). */
  private readonly _aggregate$ = new BehaviorSubject<MultiJobMonitorState | null>(null);
  readonly aggregate$: Observable<MultiJobMonitorState | null> = this._aggregate$.asObservable();

  /** Subjects de cancelamento por job. */
  private readonly cancelSubjects = new Map<string, Subject<void>>();

  /** Subscriptions de polling ativas por job. */
  private readonly pollSubs = new Map<string, Subscription>();

  /** Subscription do tick que atualiza elapsedMs do agregado. */
  private elapsedTickSub: Subscription | null = null;

  /**
   * Lista observavel de jobs ativos persistidos em localStorage (item 5).
   * Atualizada sempre que `persistActiveJob`/`removeActiveJob`/`tryResumeExecutor`
   * sao chamados. Telas usam para mostrar badge, desabilitar botoes e oferecer
   * "abrir execucao em andamento".
   */
  private readonly _activeJobs$ = new BehaviorSubject<PersistedActiveJob[]>([]);
  readonly activeJobs$: Observable<PersistedActiveJob[]> = this._activeJobs$.asObservable();

  /** Set de jobIds locais que ja tem entrada no storage (otimizacao de update). */
  private readonly persistedJobIds = new Set<string>();

  constructor(private svc: ConciliadoraService) {
    this._activeJobs$.next(this.readActiveJobs());
  }

  ngOnDestroy(): void {
    this.disposeAll();
  }

  // =====================================================================
  // API publica
  // =====================================================================

  /**
   * Agrupa registros pelo IDCONF do tipo correspondente.
   * Reaproveita logica do `agruparPorIdConf` ja existente em conciliadora-main.
   */
  groupRowsByIdConf(
    rows: ConciliadoraRegistro[],
    tipos: ConciliadoraTipoConfig[],
    contextoAcao: FlowContextoAcao,
    newModeLookup: (idConf: number) => boolean,
    buildRowPayload: (reg: ConciliadoraRegistro) => Record<string, any>,
    extra?: { nuconf?: number | null; codigoCab?: number | null; codecompany?: number | null }
  ): FlowExecutorJob[] {
    // Mapa TIPO -> config, pra anexar os metadados do PROPRIO tipo em cada linha
    // (varios tipos podem cair no mesmo IDCONF/job; antes so os metadados do
    // primeiro tipo do bucket iam, via job.metadados).
    const tipoByTipos = new Map<string, ConciliadoraTipoConfig>();
    for (const t of tipos) if (t.TIPOS) tipoByTipos.set(t.TIPOS, t);

    const buckets = new Map<number, { tipoConfig?: ConciliadoraTipoConfig; rows: ConciliadoraRegistro[] }>();
    for (const reg of rows) {
      const tipoConfig = tipos.find((t) => t.TIPOS === reg.TIPO);
      if (!tipoConfig?.IDCONF) continue;
      const id = tipoConfig.IDCONF;
      const bucket = buckets.get(id) ?? { tipoConfig, rows: [] };
      bucket.rows.push(reg);
      buckets.set(id, bucket);
    }
    const out: FlowExecutorJob[] = [];
    for (const [idConf, b] of buckets) {
      const metadados = this.parseTipoParametros(b.tipoConfig?.PARAMETROS);
      out.push({
        jobId: this.uuid(),
        idConf,
        newMode: !!newModeLookup(idConf),
        contextoAcao,
        rows: b.rows.map((reg) => {
          const base = buildRowPayload(reg);
          // metadados POR TIPO: cada linha carrega os metadados do SEU tipo, pra
          // fluxos que leem row.__metadados diferenciarem por tipo. job.metadados
          // (abaixo) segue como fallback do 1o tipo (compat com fluxo atual).
          const md = this.parseTipoParametros(tipoByTipos.get(reg.TIPO ?? '')?.PARAMETROS);
          return md ? { ...(base as Record<string, any>), __metadados: md } : base;
        }),
        metadados,
        label: `Tipo ${b.tipoConfig?.TIPOS ?? '?'} (IDCONF ${idConf}) - ${b.rows.length} reg.`,
        nuconf: extra?.nuconf ?? null,
        codigoCab: extra?.codigoCab ?? null,
        codecompany: extra?.codecompany ?? null,
        ordem: b.tipoConfig?.ORDEM ?? null,
      });
    }
    return out;
  }

  /**
   * Decisao automatica de estrategia.
   * Q1: concurrency=4 default. Q2: LEGACY_ASYNC ativo.
   */
  chooseStrategy(job: FlowExecutorJob, opts: FlowExecutorOptions): FlowExecutorStrategy {
    const forceMode = opts.forceMode ?? 'auto';
    if (forceMode === 'worker') return 'CLIENT_WORKER';
    if (forceMode === 'sync') return job.newMode ? 'NEWMODE_SYNC' : 'LEGACY_SYNC';
    if (forceMode === 'async') return job.newMode ? 'NEWMODE_ASYNC' : 'LEGACY_ASYNC';
    return job.newMode ? 'NEWMODE_ASYNC' : 'LEGACY_ASYNC';
  }

  /**
   * Executa N jobs respeitando concurrency (Q1=4). Retorna Observable que
   * completa quando todos os jobs terminam, emitindo o summary final.
   *
   * Side-effects: alimenta `aggregate$` e os state subjects de cada job.
   */
  execute(jobs: FlowExecutorJob[], opts: FlowExecutorOptions): Observable<FlowExecutorSummary> {
    const groupId = this.uuid();
    const startedAt = Date.now();
    const concurrency = opts.concurrency ?? FlowExecutorService.DEFAULT_CONCURRENCY;
    const persistHistory = opts.persistHistory !== false;
    const actionName = opts.actionName ?? jobs[0]?.contextoAcao ?? 'EXECUTAR';

    this.resetState();
    this.startAggregate(groupId, opts.title, actionName, startedAt, jobs);

    return new Observable<FlowExecutorSummary>((subscriber) => {
      const parentLogP$ = persistHistory
        ? this.createExecLogParent(actionName, opts, jobs, startedAt)
        : of<number | null>(null);

      const sub = parentLogP$
        .pipe(
          tap((idexeclogParent) => {
            const agg = this._aggregate$.getValue();
            if (agg) this._aggregate$.next({ ...agg, idexeclogParent: idexeclogParent ?? null });
          }),
          switchMap((idexeclogParent) =>
            from(jobs).pipe(
              mergeMap(
                (job) => this.runJob(job, opts, idexeclogParent, persistHistory, groupId),
                Math.max(1, concurrency)
              ),
              // Reduz para summary final apos todos terminarem
              // (mergeMap completa quando todos os internos completam)
              // -> usa scan + last() poderia ser opcao; aqui aglutinamos via array
              // Simplificado: coleta resultados e emite uma unica vez no final
              // via reduce.
              (sourceObs) => sourceObs
            )
          )
        )
        .subscribe({
          next: () => {/* runJob ja atualiza o aggregate */},
          error: (err) => {
            const summary = this.buildSummaryFromState(groupId, opts.title, actionName, startedAt);
            opts.onAllComplete?.(summary);
            subscriber.error(err);
          },
          complete: () => {
            const summary = this.buildSummaryFromState(groupId, opts.title, actionName, startedAt);
            this.finalizeAggregate(summary);
            opts.onAllComplete?.(summary);
            if (persistHistory) {
              const idParent = this._aggregate$.getValue()?.idexeclogParent;
              if (idParent != null) this.finishExecLogParent(idParent, summary);
            }
            subscriber.next(summary);
            subscriber.complete();
          },
        });

      return () => sub.unsubscribe();
    });
  }

  /** Snapshot do estado de um job individual. */
  jobState$(jobId: string): Observable<FlowMonitorState> | null {
    const sub = this.jobStates.get(jobId);
    return sub ? sub.asObservable() : null;
  }

  /** Cancela 1 job especifico. */
  cancel(jobId: string): Observable<boolean> {
    const subj = this.cancelSubjects.get(jobId);
    if (!subj) return of(false);
    subj.next();
    const state = this.jobStates.get(jobId)?.getValue();
    if (state?.idjob && (state.strategy === 'NEWMODE_ASYNC' || state.strategy === 'LEGACY_ASYNC')) {
      return this.svc.cancelarJob(state.idjob).pipe(
        map((res) => !!res?.ok),
        catchError(() => of(false))
      );
    }
    this.updateJob(jobId, (s) => ({ ...s, status: 'STOPPED', finishedAt: Date.now() }));
    return of(true);
  }

  /** Cancela todos os jobs do grupo atual. */
  cancelAll(): void {
    for (const id of Array.from(this.cancelSubjects.keys())) {
      this.cancel(id).subscribe();
    }
  }

  /** Limpa estado para um novo execute. */
  resetState(): void {
    for (const sub of this.pollSubs.values()) sub.unsubscribe();
    this.pollSubs.clear();
    for (const subj of this.cancelSubjects.values()) subj.complete();
    this.cancelSubjects.clear();
    this.jobStates.clear();
    this.elapsedTickSub?.unsubscribe();
    this.elapsedTickSub = null;
    this._aggregate$.next(null);
  }

  disposeAll(): void {
    this.resetState();
  }

  /**
   * Remove apenas os jobs que ja estao em estado terminal (COMPLETED, ERROR,
   * STOPPED, PARTIAL, NOT_FOUND). Mantem RUNNING/PENDING vivos no executor para
   * que o usuario possa fechar o drawer e reabri-lo depois (item 5 do plano).
   */
  clearTerminalJobs(): void {
    const terminalIds: string[] = [];
    for (const [id, sub] of this.jobStates) {
      if (this.isTerminal(sub.getValue().status)) terminalIds.push(id);
    }
    for (const id of terminalIds) {
      this.pollSubs.get(id)?.unsubscribe();
      this.pollSubs.delete(id);
      const cs = this.cancelSubjects.get(id);
      if (cs) {
        cs.complete();
        this.cancelSubjects.delete(id);
      }
      this.jobStates.delete(id);
    }
    if (this.jobStates.size === 0) {
      this.elapsedTickSub?.unsubscribe();
      this.elapsedTickSub = null;
      this._aggregate$.next(null);
    } else {
      this.updateAggregate();
    }
  }

  // =====================================================================
  // API publica - recuperacao / bloqueio (item 5)
  // =====================================================================

  /** Retorna snapshot dos jobs ativos persistidos. */
  getActiveJobs(): PersistedActiveJob[] {
    return this._activeJobs$.getValue();
  }

  /**
   * Verifica se existe um job ativo (PENDING/RUNNING) para uma combinacao de
   * `codigoCab` + `actionName`. Usado para desabilitar botoes "Localizar Nota"
   * e "Processar" enquanto a execucao anterior nao termina.
   */
  hasActiveJobFor(codigoCab: number | null, actionName: string): boolean {
    return !!this.getActiveJobFor(codigoCab, actionName);
  }

  /** Mesmo predicado do `hasActiveJobFor`, devolvendo o job (ou undefined). */
  getActiveJobFor(codigoCab: number | null, actionName: string): PersistedActiveJob | undefined {
    if (codigoCab == null) return undefined;
    return this._activeJobs$
      .getValue()
      .find((j) => j.codigoCab === codigoCab && j.actionName === actionName);
  }

  /**
   * Tenta retomar execucoes ativas persistidas em localStorage. Chamado uma
   * vez no `ngOnInit` da tela principal. Para cada job persistido:
   *  - Se status backend ainda RUNNING/PENDING: recria jobState, religa
   *    polling e abre o monitor.
   *  - Se status terminal (COMPLETED/ERROR/STOPPED/NOT_FOUND): apenas remove
   *    do storage (cleanup silencioso).
   *
   * Retorna o numero de jobs retomados.
   */
  tryResumeExecutor(): Observable<number> {
    const persisted = this.readActiveJobs();
    if (!persisted.length) {
      this._activeJobs$.next([]);
      return of(0);
    }

    // Tentamos status de cada um - removemos terminais; mantemos ativos.
    const checks$ = persisted.map((p) =>
      p.idjob
        ? this.svc.consultarJobStatus(p.idjob).pipe(
            map((status) => ({ p, status })),
            catchError(() =>
              of({
                p,
                status: {
                  idjob: p.idjob ?? '',
                  status: 'NOT_FOUND' as FlowJobStatusType,
                  error: false,
                  errorMessage: null,
                  startedAt: p.startedAt,
                  finishedAt: 0,
                  elapsedMs: 0,
                  result: null,
                } as FlowJobStatus,
              })
            )
          )
        : of({
            p,
            status: {
              idjob: '',
              status: 'NOT_FOUND' as FlowJobStatusType,
              error: false,
              errorMessage: null,
              startedAt: p.startedAt,
              finishedAt: 0,
              elapsedMs: 0,
              result: null,
            } as FlowJobStatus,
          })
    );

    return forkJoin(checks$).pipe(
      map((results) => {
        const alive = results.filter(
          (r) => r.status.status === 'PENDING' || r.status.status === 'RUNNING'
        );
        // Limpa terminais do storage.
        for (const r of results) {
          if (r.status.status !== 'PENDING' && r.status.status !== 'RUNNING') {
            this.persistedJobIds.delete(r.p.jobId);
          }
        }

        if (alive.length === 0) {
          this.writeActiveJobs([]);
          this._activeJobs$.next([]);
          return 0;
        }

        // Reconstroi jobStates dos vivos e reata o polling.
        // Agrupa pelo groupId para reaproveitar o aggregate$ (1 grupo por rodada).
        // No caso comum (1 acao -> 1+ jobs), todos compartilham o mesmo groupId.
        const groupId = alive[0].p.groupId;
        const startedAt = Math.min(...alive.map((a) => a.p.startedAt));
        const title = alive[0].p.title;
        const actionName = alive[0].p.actionName;

        // Limpa jobStates anteriores e inicia agregado novo.
        this.resetState();

        this._aggregate$.next({
          groupId,
          title,
          actionName,
          startedAt,
          finishedAt: null,
          elapsedMs: Date.now() - startedAt,
          status: 'RUNNING',
          totals: {
            jobs: alive.length,
            completed: 0,
            failed: 0,
            cancelled: 0,
            rowsCount: alive.reduce((s, a) => s + a.p.rowsCount, 0),
            rowsOk: 0,
            rowsErr: 0,
          },
          jobIds: alive.map((a) => a.p.jobId),
          idexeclogParent: null,
        });
        this.elapsedTickSub?.unsubscribe();
        this.elapsedTickSub = timer(0, 1000).subscribe(() => {
          const a = this._aggregate$.getValue();
          if (!a || a.finishedAt) return;
          this._aggregate$.next({ ...a, elapsedMs: Date.now() - a.startedAt });
        });

        // Recria o state e religa o polling para cada job vivo.
        for (const { p, status } of alive) {
          const cancel$ = new Subject<void>();
          this.cancelSubjects.set(p.jobId, cancel$);
          const initial: FlowMonitorState = {
            idjob: p.idjob,
            threadName: p.threadName,
            idconf: p.idConf,
            status: status.status,
            error: !!status.error,
            errorMessage: status.errorMessage ?? null,
            steps: [],
            rawResult: null,
            startedAt: status.startedAt > 0 ? status.startedAt : p.startedAt,
            finishedAt: status.finishedAt > 0 ? status.finishedAt : null,
            elapsedMs: status.elapsedMs ?? Date.now() - p.startedAt,
            rowsCount: p.rowsCount,
            title: p.title,
            contextoAcao: (p.actionName as any) ?? null,
            starting: false,
            jobId: p.jobId,
            label: p.label,
            rowsOk: 0,
            rowsErr: 0,
            strategy: p.strategy,
            idexeclog: p.idexeclog ?? null,
            readonly: false,
          };
          this.jobStates.set(p.jobId, new BehaviorSubject<FlowMonitorState>(initial));
          if (p.idjob) {
            // Religa polling. takeUntil cancel$ ja eh tratado dentro.
            this.pollJob(p.jobId, p.idjob, cancel$).subscribe({
              complete: () => {
                this.removeActiveJob(p.jobId);
                this.updateAggregate();
              },
            });
          }
        }
        this.updateAggregate();
        // Reatualiza o storage so com os vivos.
        this.writeActiveJobs(alive.map(({ p }) => p));
        this._activeJobs$.next(alive.map(({ p }) => p));
        return alive.length;
      })
    );
  }

  // =====================================================================
  // localStorage helpers (item 5)
  // =====================================================================

  private readActiveJobs(): PersistedActiveJob[] {
    try {
      const raw = localStorage.getItem(FlowExecutorService.STORAGE_KEY_GROUPS);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      this.persistedJobIds.clear();
      for (const j of parsed) {
        if (j && typeof j.jobId === 'string') this.persistedJobIds.add(j.jobId);
      }
      return parsed as PersistedActiveJob[];
    } catch {
      return [];
    }
  }

  private writeActiveJobs(jobs: PersistedActiveJob[]): void {
    try {
      localStorage.setItem(
        FlowExecutorService.STORAGE_KEY_GROUPS,
        JSON.stringify(jobs)
      );
      this.persistedJobIds.clear();
      for (const j of jobs) this.persistedJobIds.add(j.jobId);
    } catch {
      /* quota/iframe - degrade silently */
    }
  }

  private persistActiveJob(entry: PersistedActiveJob): void {
    const all = this.readActiveJobs();
    const idx = all.findIndex((x) => x.jobId === entry.jobId);
    if (idx >= 0) all[idx] = { ...all[idx], ...entry };
    else all.push(entry);
    this.writeActiveJobs(all);
    this._activeJobs$.next(all);
  }

  private updatePersistedJob(jobId: string, patch: Partial<PersistedActiveJob>): void {
    if (!this.persistedJobIds.has(jobId)) return;
    const all = this.readActiveJobs();
    const idx = all.findIndex((x) => x.jobId === jobId);
    if (idx < 0) return;
    all[idx] = { ...all[idx], ...patch };
    this.writeActiveJobs(all);
    this._activeJobs$.next(all);
  }

  private removeActiveJob(jobId: string): void {
    if (!this.persistedJobIds.has(jobId)) return;
    const all = this.readActiveJobs().filter((j) => j.jobId !== jobId);
    this.writeActiveJobs(all);
    this._activeJobs$.next(all);
  }

  // =====================================================================
  // Execucao por job - chave do servico
  // =====================================================================

  private runJob(
    job: FlowExecutorJob,
    opts: FlowExecutorOptions,
    idexeclogParent: number | null,
    persistHistory: boolean,
    groupId: string
  ): Observable<FlowExecutorJobResult> {
    const strategy = this.chooseStrategy(job, opts);
    const cancel$ = new Subject<void>();
    this.cancelSubjects.set(job.jobId, cancel$);

    const startedAt = Date.now();

    // Persiste o job no localStorage para recuperacao (item 5).
    const persistedEntry: PersistedActiveJob = {
      jobId: job.jobId,
      groupId,
      idjob: null,
      threadName: null,
      idConf: job.idConf,
      actionName: opts.actionName ?? job.contextoAcao,
      strategy,
      codigoCab: job.codigoCab ?? null,
      nuconf: job.nuconf ?? null,
      codecompany: job.codecompany ?? null,
      label: job.label,
      title: opts.title,
      rowsCount: job.rows.length,
      status: 'PENDING',
      startedAt,
      idexeclog: null,
    };
    this.persistActiveJob(persistedEntry);
    const initial: FlowMonitorState = {
      idjob: null,
      threadName: null,
      idconf: job.idConf,
      status: 'PENDING',
      error: false,
      errorMessage: null,
      steps: [],
      rawResult: null,
      startedAt,
      finishedAt: null,
      elapsedMs: 0,
      rowsCount: job.rows.length,
      title: job.label,
      contextoAcao: job.contextoAcao,
      starting: true,
      jobId: job.jobId,
      label: job.label,
      rowsOk: 0,
      rowsErr: 0,
      strategy,
      idexeclog: null,
      readonly: false,
    };
    const jobSubject = new BehaviorSubject<FlowMonitorState>(initial);
    this.jobStates.set(job.jobId, jobSubject);
    this.updateAggregate();

    const childLog$: Observable<number | null> = persistHistory
      ? this.createExecLogChild(job, opts, idexeclogParent, strategy)
      : of<number | null>(null);

    return childLog$.pipe(
      switchMap((idexeclog) => {
        this.updateJob(job.jobId, (s) => ({ ...s, idexeclog: idexeclog ?? null }));
        if (idexeclog != null) {
          this.updatePersistedJob(job.jobId, { idexeclog });
        }
        switch (strategy) {
          case 'NEWMODE_ASYNC':
            return this.runNewModeAsync(job, opts, cancel$);
          case 'LEGACY_ASYNC':
            return this.runLegacyAsync(job, opts, cancel$);
          case 'NEWMODE_SYNC':
            return this.runNewModeSync(job, opts, cancel$);
          case 'LEGACY_SYNC':
            return this.runLegacySync(job, opts, cancel$);
          case 'CLIENT_WORKER':
            return this.runClientWorker(job, opts, cancel$);
        }
      }),
      takeUntil(cancel$),
      tap((result) => {
        opts.onJobComplete?.(job, result);
        if (persistHistory) {
          const s = this.jobStates.get(job.jobId)?.getValue();
          if (s?.idexeclog != null) this.finishExecLogChild(s.idexeclog, result);
        }
      }),
      catchError((err) => {
        const errorMsg = err?.message ?? String(err);
        this.updateJob(job.jobId, (s) => ({
          ...s,
          status: 'ERROR',
          error: true,
          errorMessage: errorMsg,
          finishedAt: Date.now(),
          elapsedMs: Date.now() - (s.startedAt ?? Date.now()),
        }));
        const result = this.jobResultFromState(job, strategy);
        if (persistHistory) {
          const s = this.jobStates.get(job.jobId)?.getValue();
          if (s?.idexeclog != null) this.finishExecLogChild(s.idexeclog, result);
        }
        return of(result);
      }),
      finalize(() => {
        this.pollSubs.get(job.jobId)?.unsubscribe();
        this.pollSubs.delete(job.jobId);
        // Job terminou (qualquer caminho: success, error, cancel) - remove da
        // persistencia para nao oferecer "reabrir" depois (item 5).
        this.removeActiveJob(job.jobId);
        this.updateAggregate();
      })
    );
  }

  // =====================================================================
  // Estrategia: NEWMODE_ASYNC (via runOnlineAsync + polling)
  // =====================================================================

  private runNewModeAsync(
    job: FlowExecutorJob,
    opts: FlowExecutorOptions,
    cancel$: Subject<void>
  ): Observable<FlowExecutorJobResult> {
    const execOpts = this.toExecutarOpts(job, opts);
    return this.svc.iniciarFlowAsync(job.idConf, job.rows, execOpts).pipe(
      switchMap((start) => {
        if (!start?.ok || !start.idjob) {
          throw new Error(start?.message ?? 'Backend nao retornou idjob valido.');
        }
        this.updateJob(job.jobId, (s) => ({
          ...s,
          idjob: start.idjob!,
          threadName: start.threadName ?? null,
          starting: false,
          status: (start.status as FlowJobStatusType) ?? 'PENDING',
        }));
        this.updatePersistedJob(job.jobId, {
          idjob: start.idjob!,
          threadName: start.threadName ?? null,
          status: (start.status as 'PENDING' | 'RUNNING') ?? 'PENDING',
        });
        return this.pollJob(job.jobId, start.idjob!, cancel$);
      }),
      map(() => this.jobResultFromState(job, 'NEWMODE_ASYNC'))
    );
  }

  // =====================================================================
  // Estrategia: LEGACY_ASYNC (via runActionAsync + polling)
  // Fallback: LEGACY_SYNC se backend nao tem o endpoint (404 / 500).
  // =====================================================================

  private runLegacyAsync(
    job: FlowExecutorJob,
    opts: FlowExecutorOptions,
    cancel$: Subject<void>
  ): Observable<FlowExecutorJobResult> {
    const execOpts = this.toExecutarOpts(job, opts);
    return this.svc.iniciarActionAsync(job.idConf, job.rows, execOpts).pipe(
      switchMap((start) => {
        if (!start?.ok || !start.idjob) {
          throw new Error(start?.message ?? 'Backend nao retornou idjob valido (Legacy).');
        }
        this.updateJob(job.jobId, (s) => ({
          ...s,
          idjob: start.idjob!,
          threadName: start.threadName ?? null,
          starting: false,
          status: (start.status as FlowJobStatusType) ?? 'PENDING',
        }));
        this.updatePersistedJob(job.jobId, {
          idjob: start.idjob!,
          threadName: start.threadName ?? null,
          status: (start.status as 'PENDING' | 'RUNNING') ?? 'PENDING',
        });
        return this.pollJob(job.jobId, start.idjob!, cancel$);
      }),
      map(() => this.jobResultFromState(job, 'LEGACY_ASYNC')),
      catchError((err) => {
        if (opts.fallbackToSyncOnUnavailable === false) throw err;
        const msg = String(err?.message ?? '');
        const looksLikeMissingEndpoint =
          msg.includes('404') || msg.toLowerCase().includes('not found') ||
          msg.toLowerCase().includes('runactionasync');
        if (!looksLikeMissingEndpoint) throw err;
        // Fallback transparente
        this.updateJob(job.jobId, (s) => ({
          ...s,
          strategy: 'LEGACY_SYNC',
          starting: true,
          errorMessage: 'runActionAsync indisponivel; fallback para LEGACY_SYNC.',
        }));
        return this.runLegacySync(job, opts, cancel$);
      })
    );
  }

  // =====================================================================
  // Estrategia: NEWMODE_SYNC (chunks via /runOnline)
  // =====================================================================

  private runNewModeSync(
    job: FlowExecutorJob,
    opts: FlowExecutorOptions,
    cancel$: Subject<void>
  ): Observable<FlowExecutorJobResult> {
    const execOpts = this.toExecutarOpts(job, opts);
    this.updateJob(job.jobId, (s) => ({ ...s, starting: false, status: 'RUNNING' }));
    return this.svc.executarFlowOnline(job.idConf, job.rows, execOpts).pipe(
      takeUntil(cancel$),
      map((res: any) => {
        const total = Number(res?.rowsCount ?? job.rows.length);
        this.updateJob(job.jobId, (s) => ({
          ...s,
          status: 'COMPLETED',
          rowsOk: total,
          rowsErr: 0,
          finishedAt: Date.now(),
          elapsedMs: Date.now() - (s.startedAt ?? Date.now()),
          rawResult: res,
        }));
        return this.jobResultFromState(job, 'NEWMODE_SYNC');
      })
    );
  }

  // =====================================================================
  // Estrategia: LEGACY_SYNC (mergeMap por linha com concurrency)
  // =====================================================================

  private runLegacySync(
    job: FlowExecutorJob,
    opts: FlowExecutorOptions,
    cancel$: Subject<void>
  ): Observable<FlowExecutorJobResult> {
    const execOpts = this.toExecutarOpts(job, opts);
    const concurrency = opts.concurrency ?? FlowExecutorService.DEFAULT_CONCURRENCY;
    this.updateJob(job.jobId, (s) => ({ ...s, starting: false, status: 'RUNNING' }));

    return new Observable<FlowExecutorJobResult>((subscriber) => {
      let ok = 0;
      let err = 0;
      const sub = from(job.rows)
        .pipe(
          takeUntil(cancel$),
          mergeMap(
            (row) =>
              this.svc.executarConfiguracao(job.idConf, row, { ...execOpts, newMode: false }).pipe(
                map(() => ({ success: true as const })),
                catchError(() => of({ success: false as const }))
              ),
            Math.max(1, concurrency)
          )
        )
        .subscribe({
          next: (r) => {
            if (r.success) ok++;
            else err++;
            this.updateJob(job.jobId, (s) => ({
              ...s,
              rowsOk: ok,
              rowsErr: err,
              elapsedMs: Date.now() - (s.startedAt ?? Date.now()),
            }));
          },
          error: (e) => subscriber.error(e),
          complete: () => {
            const finalStatus: FlowJobStatusType =
              err === 0 ? 'COMPLETED' : ok === 0 ? 'ERROR' : ('PARTIAL' as any);
            this.updateJob(job.jobId, (s) => ({
              ...s,
              status: finalStatus,
              finishedAt: Date.now(),
              elapsedMs: Date.now() - (s.startedAt ?? Date.now()),
              error: err > 0 && ok === 0,
            }));
            subscriber.next(this.jobResultFromState(job, 'LEGACY_SYNC'));
            subscriber.complete();
          },
        });
      return () => sub.unsubscribe();
    });
  }

  // =====================================================================
  // Estrategia: CLIENT_WORKER (Calcular Provisao - Fase 7)
  // STUB nesta fase. Sera completado quando o worker for criado.
  // =====================================================================

  private runClientWorker(
    job: FlowExecutorJob,
    opts: FlowExecutorOptions,
    cancel$: Subject<void>
  ): Observable<FlowExecutorJobResult> {
    this.updateJob(job.jobId, (s) => ({
      ...s,
      starting: false,
      status: 'ERROR',
      error: true,
      errorMessage:
        'CLIENT_WORKER ainda nao implementado. Fase 7 do plano (provisao-calc.worker.ts).',
      finishedAt: Date.now(),
    }));
    return of(this.jobResultFromState(job, 'CLIENT_WORKER'));
  }

  // =====================================================================
  // Polling generico (NEWMODE_ASYNC + LEGACY_ASYNC)
  // =====================================================================

  private pollJob(jobId: string, idjob: string, cancel$: Subject<void>): Observable<void> {
    return new Observable<void>((subscriber) => {
      const sub = timer(0, FlowExecutorService.POLL_INTERVAL_MS)
        .pipe(
          takeUntil(cancel$),
          switchMap(() =>
            this.svc.consultarJobStatus(idjob).pipe(
              catchError((err) =>
                of<FlowJobStatus>({
                  idjob,
                  status: 'ERROR',
                  error: true,
                  errorMessage: err?.message ?? 'Falha de comunicacao com o servidor.',
                  startedAt: 0,
                  finishedAt: Date.now(),
                  elapsedMs: 0,
                  result: null,
                })
              )
            )
          )
        )
        .subscribe({
          next: (status) => {
            this.applyStatusToJob(jobId, status);
            if (this.isTerminal(status.status)) {
              subscriber.next();
              subscriber.complete();
            }
          },
          error: (e) => subscriber.error(e),
        });
      this.pollSubs.set(jobId, sub);
      return () => sub.unsubscribe();
    });
  }

  private applyStatusToJob(jobId: string, status: FlowJobStatus): void {
    this.updateJob(jobId, (current) => {
      const next: FlowMonitorState = {
        ...current,
        status: status.status,
        error: !!status.error,
        errorMessage: status.errorMessage ?? current.errorMessage,
        elapsedMs: status.elapsedMs ?? current.elapsedMs,
        startedAt: status.startedAt > 0 ? status.startedAt : current.startedAt,
        finishedAt: status.finishedAt > 0 ? status.finishedAt : current.finishedAt,
        threadName: status.threadName ?? current.threadName,
        rawResult: status.result ?? current.rawResult,
      };
      if (status.status === 'NOT_FOUND') {
        next.status = 'ERROR';
        next.error = true;
        next.errorMessage = 'Job nao encontrado (pode ter expirado).';
        next.finishedAt = Date.now();
      }
      if (this.isTerminal(next.status)) {
        next.steps = this.parseStepsFromResult(status.result);
        // Tenta extrair rowsOk/rowsErr do result (LEGACY_ASYNC retorna esses campos)
        const raw = status.result;
        if (raw && typeof raw === 'object') {
          if (typeof raw.rowsOk === 'number') next.rowsOk = raw.rowsOk;
          if (typeof raw.rowsErr === 'number') next.rowsErr = raw.rowsErr;
        }
        // Completed silencioso: algum step com error
        if (next.status === 'COMPLETED') {
          const failed = (next.steps ?? []).filter((s) => s.error);
          if (failed.length > 0) {
            next.status = 'ERROR';
            next.error = true;
            next.errorMessage = `Fluxo terminou mas ${failed.length} step(s) falharam: ${failed[0].errorMessage ?? 'success=false'}.`;
          }
        }
      }
      return next;
    });
  }

  // =====================================================================
  // Agregado e estado
  // =====================================================================

  private startAggregate(
    groupId: string,
    title: string,
    actionName: string,
    startedAt: number,
    jobs: FlowExecutorJob[]
  ): void {
    this._aggregate$.next({
      groupId,
      title,
      actionName,
      startedAt,
      finishedAt: null,
      elapsedMs: 0,
      status: 'PENDING',
      totals: {
        jobs: jobs.length,
        completed: 0,
        failed: 0,
        cancelled: 0,
        rowsCount: jobs.reduce((s, j) => s + j.rows.length, 0),
        rowsOk: 0,
        rowsErr: 0,
      },
      jobIds: jobs.map((j) => j.jobId),
      idexeclogParent: null,
    });
    this.elapsedTickSub?.unsubscribe();
    this.elapsedTickSub = timer(0, 1000).subscribe(() => {
      const a = this._aggregate$.getValue();
      if (!a || a.finishedAt) return;
      this._aggregate$.next({ ...a, elapsedMs: Date.now() - a.startedAt });
    });
  }

  private finalizeAggregate(summary: FlowExecutorSummary): void {
    const a = this._aggregate$.getValue();
    if (!a) return;
    let status: MultiJobMonitorState['status'] = 'COMPLETED';
    if (summary.cancelled > 0) status = 'STOPPED';
    else if (summary.failed === summary.totalJobs) status = 'ERROR';
    else if (summary.failed > 0 || summary.partial > 0) status = 'PARTIAL';
    this._aggregate$.next({
      ...a,
      status,
      finishedAt: summary.finishedAt ?? Date.now(),
      elapsedMs: summary.elapsedMs,
      totals: {
        ...a.totals,
        completed: summary.completed,
        failed: summary.failed,
        cancelled: summary.cancelled,
        rowsOk: summary.totalRowsOk,
        rowsErr: summary.totalRowsErr,
      },
    });
    this.elapsedTickSub?.unsubscribe();
    this.elapsedTickSub = null;
  }

  private updateAggregate(): void {
    const a = this._aggregate$.getValue();
    if (!a) return;
    let completed = 0;
    let failed = 0;
    let cancelled = 0;
    let rowsOk = 0;
    let rowsErr = 0;
    let running = false;
    for (const sub of this.jobStates.values()) {
      const s = sub.getValue();
      rowsOk += s.rowsOk ?? 0;
      rowsErr += s.rowsErr ?? 0;
      if (s.status === 'COMPLETED') completed++;
      else if (s.status === 'ERROR') failed++;
      else if (s.status === 'STOPPED') cancelled++;
      else if (s.status === 'RUNNING' || s.status === 'PENDING' || s.starting) running = true;
    }
    const totalDone = completed + failed + cancelled;
    let status: MultiJobMonitorState['status'] = 'PENDING';
    if (totalDone === 0 && running) status = 'PENDING';
    else if (running) status = 'RUNNING';
    else if (totalDone === a.totals.jobs) {
      if (cancelled > 0) status = 'STOPPED';
      else if (failed === a.totals.jobs) status = 'ERROR';
      else if (failed > 0) status = 'PARTIAL';
      else status = 'COMPLETED';
    }
    this._aggregate$.next({
      ...a,
      status,
      totals: { ...a.totals, completed, failed, cancelled, rowsOk, rowsErr },
    });
  }

  private updateJob(jobId: string, fn: (s: FlowMonitorState) => FlowMonitorState): void {
    const sub = this.jobStates.get(jobId);
    if (!sub) return;
    const next = fn(sub.getValue());
    sub.next(next);
    this.updateAggregate();
  }

  private jobResultFromState(job: FlowExecutorJob, strategy: FlowExecutorStrategy): FlowExecutorJobResult {
    const s = this.jobStates.get(job.jobId)?.getValue();
    return {
      jobId: job.jobId,
      idConf: job.idConf,
      strategy,
      status: s?.status ?? 'IDLE',
      rowsCount: job.rows.length,
      rowsOk: s?.rowsOk ?? 0,
      rowsErr: s?.rowsErr ?? 0,
      startedAt: s?.startedAt ?? null,
      finishedAt: s?.finishedAt ?? null,
      elapsedMs: s?.elapsedMs ?? 0,
      errorMessage: s?.errorMessage ?? null,
      steps: s?.steps ?? [],
      idexeclog: s?.idexeclog ?? null,
    };
  }

  private buildSummaryFromState(
    groupId: string,
    title: string,
    actionName: string,
    startedAt: number
  ): FlowExecutorSummary {
    let completed = 0;
    let failed = 0;
    let cancelled = 0;
    let partial = 0;
    let totalRowsCount = 0;
    let totalRowsOk = 0;
    let totalRowsErr = 0;
    const jobs: FlowExecutorJobResult[] = [];
    for (const sub of this.jobStates.values()) {
      const s = sub.getValue();
      const r: FlowExecutorJobResult = {
        jobId: s.jobId ?? '',
        idConf: s.idconf ?? 0,
        strategy: (s.strategy as FlowExecutorStrategy) ?? 'NEWMODE_ASYNC',
        status: s.status,
        rowsCount: s.rowsCount,
        rowsOk: s.rowsOk ?? 0,
        rowsErr: s.rowsErr ?? 0,
        startedAt: s.startedAt,
        finishedAt: s.finishedAt,
        elapsedMs: s.elapsedMs,
        errorMessage: s.errorMessage,
        steps: s.steps,
        idexeclog: s.idexeclog ?? null,
      };
      jobs.push(r);
      totalRowsCount += r.rowsCount;
      totalRowsOk += r.rowsOk;
      totalRowsErr += r.rowsErr;
      if (s.status === 'COMPLETED') completed++;
      else if (s.status === 'ERROR') failed++;
      else if (s.status === 'STOPPED') cancelled++;
      else if (s.status === ('PARTIAL' as any)) partial++;
    }
    const finishedAt = Date.now();
    return {
      totalJobs: jobs.length,
      completed,
      failed,
      cancelled,
      partial,
      totalRowsCount,
      totalRowsOk,
      totalRowsErr,
      startedAt,
      finishedAt,
      elapsedMs: finishedAt - startedAt,
      idexeclogParent: this._aggregate$.getValue()?.idexeclogParent ?? null,
      jobs,
    };
  }

  // =====================================================================
  // Persistencia em MKTPFLOWEXECLOG (Q3) - best-effort
  // =====================================================================

  private createExecLogParent(
    actionName: string,
    opts: FlowExecutorOptions,
    jobs: FlowExecutorJob[],
    startedAt: number
  ): Observable<number | null> {
    const totalRows = jobs.reduce((s, j) => s + j.rows.length, 0);
    const req: FlowExecLogStartRequest = {
      idexeclogparent: null,
      codecompany: jobs[0]?.codecompany ?? 0,
      username: '',
      screenorigin: opts.screenOrigin ?? 'CONCILIADORA',
      actionname: actionName,
      idconf: jobs.length === 1 ? jobs[0].idConf : null,
      strategy: jobs[0] ? this.chooseStrategy(jobs[0], opts) : 'NEWMODE_ASYNC',
      label: opts.title,
      status: 'RUNNING',
      rowscount: totalRows,
      nuconf: jobs[0]?.nuconf ?? null,
      codigocab: jobs[0]?.codigoCab ?? null,
      payload: JSON.stringify({
        jobs: jobs.map((j) => ({
          jobId: j.jobId,
          idConf: j.idConf,
          newMode: j.newMode,
          rowsCount: j.rows.length,
          label: j.label,
        })),
        startedAt,
      }),
    };
    return this.svc.flowExecLogStart(req).pipe(
      map((res) => res.idexeclog ?? null),
      catchError(() => of<number | null>(null))
    );
  }

  private createExecLogChild(
    job: FlowExecutorJob,
    opts: FlowExecutorOptions,
    parent: number | null,
    strategy: FlowExecutorStrategy
  ): Observable<number | null> {
    const req: FlowExecLogStartRequest = {
      idexeclogparent: parent,
      codecompany: job.codecompany ?? 0,
      username: '',
      screenorigin: opts.screenOrigin ?? 'CONCILIADORA',
      actionname: opts.actionName ?? job.contextoAcao,
      idconf: job.idConf,
      strategy,
      label: job.label,
      status: 'RUNNING',
      rowscount: job.rows.length,
      nuconf: job.nuconf ?? null,
      codigocab: job.codigoCab ?? null,
      payload: JSON.stringify({
        jobId: job.jobId,
        metadados: job.metadados,
        rowsCount: job.rows.length,
      }),
    };
    return this.svc.flowExecLogStart(req).pipe(
      map((res) => res.idexeclog ?? null),
      catchError(() => of<number | null>(null))
    );
  }

  private finishExecLogChild(idexeclog: number, result: FlowExecutorJobResult): void {
    const flowJson = result.steps && result.steps.length
      ? JSON.stringify(result.steps).substring(0, 1_048_576)
      : null;
    this.svc
      .flowExecLogFinish({
        idexeclog,
        status: this.mapStatusToExecLog(result.status),
        rowsok: result.rowsOk,
        rowserr: result.rowsErr,
        errormsg: result.errorMessage ?? null,
        elapsedms: result.elapsedMs,
        resultsummary: `${result.rowsOk} OK, ${result.rowsErr} erro${result.rowsErr === 1 ? '' : 's'}`,
        flowexecutionjson: flowJson,
      })
      .pipe(catchError(() => of(null)))
      .subscribe();
  }

  private finishExecLogParent(idexeclog: number, summary: FlowExecutorSummary): void {
    let status: FlowExecLogStartRequest['status'] = 'COMPLETED';
    if (summary.cancelled > 0) status = 'STOPPED';
    else if (summary.failed === summary.totalJobs) status = 'ERROR';
    else if (summary.failed > 0 || summary.partial > 0) status = 'PARTIAL';
    this.svc
      .flowExecLogFinish({
        idexeclog,
        status: status as any,
        rowsok: summary.totalRowsOk,
        rowserr: summary.totalRowsErr,
        errormsg: null,
        elapsedms: summary.elapsedMs,
        resultsummary: `${summary.completed}/${summary.totalJobs} jobs OK | ${summary.totalRowsOk}/${summary.totalRowsCount} reg. OK`,
        flowexecutionjson: null,
      })
      .pipe(catchError(() => of(null)))
      .subscribe();
  }

  private mapStatusToExecLog(status: FlowJobStatusType | 'IDLE' | 'PARTIAL'): FlowExecLogFinishRequest['status'] {
    if (status === 'COMPLETED') return 'COMPLETED';
    if (status === 'STOPPED') return 'STOPPED';
    if (status === ('PARTIAL' as any)) return 'PARTIAL';
    return 'ERROR';
  }

  // =====================================================================
  // Utilitarios
  // =====================================================================

  private toExecutarOpts(job: FlowExecutorJob, opts?: FlowExecutorOptions): ExecutarOpts {
    return {
      newMode: job.newMode,
      contextoAcao: job.contextoAcao,
      metadados: job.metadados ?? null,
      nuconf: job.nuconf ?? null,
      codigoCab: job.codigoCab ?? null,
      codecompany: job.codecompany ?? null,
      logEnabled: opts?.logEnabled ?? true,
    };
  }

  private parseTipoParametros(p: string | null | undefined): Record<string, any> | null {
    if (!p) return null;
    try { return JSON.parse(p); } catch { return null; }
  }

  private parseStepsFromResult(result: any): FlowExecutionStep[] {
    if (!result) return [];
    let raw: any = result;
    if (typeof raw === 'string') {
      try { raw = JSON.parse(raw); } catch { return []; }
    }
    if (!raw || typeof raw !== 'object') return [];
    const steps: FlowExecutionStep[] = [];
    if (Array.isArray(raw)) {
      for (const item of raw) this.collectStep(item, steps);
    } else {
      for (const key of Object.keys(raw)) {
        const value = raw[key];
        if (Array.isArray(value)) {
          for (const item of value) this.collectStep(item, steps, key);
        } else if (value && typeof value === 'object') {
          this.collectStep(value, steps, key);
        }
      }
    }
    steps.sort((a, b) => (a.dhStart ?? '').localeCompare(b.dhStart ?? ''));
    return steps;
  }

  private collectStep(item: any, out: FlowExecutionStep[], fallbackKey?: string): void {
    if (!item || typeof item !== 'object') return;
    const innerExecutions: any[] = Array.isArray(item.result)
      ? item.result
      : Array.isArray(item.outPut)
        ? item.outPut
        : [];
    const hasUnsuccessful = innerExecutions.some((r) => r && typeof r === 'object' && r.success === false);
    out.push({
      key: String(item.guid ?? item.key ?? item.stepKey ?? fallbackKey ?? ''),
      name: item.name ?? item.stepName ?? item.label,
      type: item.type ?? item.stepType,
      dhStart: item.dhStart,
      dhEnd: item.dhEnd,
      durationMs: typeof item.durationMs === 'number' ? item.durationMs : undefined,
      parentExecutionId: item.parentExecutionId,
      executionPath: item.executionPath,
      input: item.inputParameter ?? item.input ?? item.inputParameters,
      output: item.outputParameter ?? item.output ?? item.outputParameters,
      error: !!item.error || hasUnsuccessful,
      errorMessage: item.errorMessage,
    });
  }

  private isTerminal(status: FlowJobStatusType | 'IDLE' | 'PARTIAL'): boolean {
    return (
      status === 'COMPLETED' ||
      status === 'ERROR' ||
      status === 'STOPPED' ||
      status === 'PARTIAL'
    );
  }

  private uuid(): string {
    if (typeof crypto !== 'undefined' && typeof (crypto as any).randomUUID === 'function') {
      return (crypto as any).randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }
}
